'use client';

import { useState, useEffect, useCallback } from 'react';

interface ConnectionStatus {
  online: boolean;
  relayAvailable: boolean;
  serverReachable: boolean;
}

const HEALTH_URL = '/api/health';
const RELAY_HEALTH_URL = 'http://localhost:8081';
const POLL_INTERVAL = 15_000;

export function useConnectionStatus(): ConnectionStatus {
  const [status, setStatus] = useState<ConnectionStatus>({
    // MUST match the SSR output on the first client render. Reading
    // navigator.onLine here was a hydration hazard: the server always renders
    // "online" (banner absent), so a browser reporting offline at hydration
    // instant made React pair the injected banner against the workspace shell
    // (seen live 2026-08-08 during a dev-pod cold boot). checkConnectivity()
    // and the offline listener correct real state within a tick of mount.
    online: true,
    relayAvailable: false,
    serverReachable: true,
  });

  const checkConnectivity = useCallback(async () => {
    const online = navigator.onLine;

    let serverReachable = false;
    if (online) {
      try {
        const res = await fetch(HEALTH_URL, { method: 'HEAD', cache: 'no-store' });
        serverReachable = res.ok;
      } catch {
        serverReachable = false;
      }
    }

    let relayAvailable = false;
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (isLocal) {
      try {
        const res = await fetch(RELAY_HEALTH_URL, { method: 'GET', cache: 'no-store', signal: AbortSignal.timeout(2000) });
        relayAvailable = res.ok;
      } catch {
        relayAvailable = false;
      }
    }

    setStatus({ online, relayAvailable, serverReachable });
  }, []);

  useEffect(() => {
    checkConnectivity();

    const onOnline = () => { setStatus((s) => ({ ...s, online: true })); checkConnectivity(); };
    const onOffline = () => { setStatus((s) => ({ ...s, online: false, serverReachable: false })); };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const interval = setInterval(checkConnectivity, POLL_INTERVAL);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(interval);
    };
  }, [checkConnectivity]);

  return status;
}
